import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import Autosuggest, {
  AutosuggestListOption,
} from "@/core/components/Autosuggest/Autosuggest";
import { FieldProps } from "@/core/components/Field";
import { AddressData } from "@/core/types/types";
import { getAddresses } from "@/MIE/api/addressesApi";

export const AddressesAutosuggest = ({ ...props }: FieldProps) => {
  return (
    <Autosuggest
      {...props}
      minLength={3}
      loadingText={t("addressAutocomplete.notFound")}
      handleDefaultValue={({
        currentDefaultValue,
        setInputValue,
        setAutosuggestInput,
        setValue,
      }) => {
        if (
          typeof currentDefaultValue === "object" &&
          isAddress(currentDefaultValue)
        ) {
          setInputValue(stringifyAddress(currentDefaultValue));
          setAutosuggestInput(stringifyAddress(currentDefaultValue));
          setValue(props.field.key, currentDefaultValue);
        }
      }}
      getData={async (input: string) => {
        const { results } = await getAddresses(input);
        return results;
      }}
      getInputFromOption={(option) => {
        if (isAddress(option)) {
          return stringifyAddress(option);
        }
      }}
      filterOptions={filterAddressOptions}
      getLabelForOption={(option) => {
        if (isAddress(option)) {
          return stringifyAddress(option);
        }

        return option.toString();
      }}
      isOptionEqualToValue={(
        option: AutosuggestListOption,
        autosuggestInput: string
      ) => {
        if ("street" in option) {
          return option.street
            .toLowerCase()
            .startsWith(autosuggestInput.toLowerCase());
        }

        return false;
      }}
    />
  );
};

function filterAddressOptions(options: AutosuggestListOption[], input: string) {
  if (isAddressList(options)) {
    const filteredOptions = options.filter((option) =>
      stringifyAddress(option).toLowerCase().startsWith(input.toLowerCase())
    );

    return filteredOptions.sort((a, b) => {
      const {
        houseNumber: houseNumberA,
        houseNumberAddition: houseNumberAdditionA,
      } = a;
      const {
        houseNumber: houseNumberB,
        houseNumberAddition: houseNumberAdditionB,
      } = b;

      // 55B, 55C...
      if (
        houseNumberAdditionA &&
        houseNumberAdditionB &&
        houseNumberA === houseNumberB
      ) {
        return houseNumberAdditionA.localeCompare(houseNumberAdditionB);
      }

      // Sort number ascending
      return parseInt(houseNumberA) - parseInt(houseNumberB);
    });
  }

  return options;
}

function isAddressList(
  options: AutosuggestListOption[]
): options is AddressData[] {
  return options.every(isAddress);
}

function isAddress(
  option: AutosuggestListOption | null
): option is AddressData {
  return !!option && "street" in option;
}

function stringifyAddress(address: AddressData) {
  return `${address.street} ${address.houseNumberFull}, ${address.postcode} ${address.city}`;
}
